import { Component, lazy, Suspense, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import LanguageSwitcher from './components/LanguageSwitcher.jsx'
import { LoadingCard } from './components/GameUI.jsx'
import {
  completeGameAssetLoad,
  forceGameAssetRefresh,
  recoverGameAssetLoad,
  waitForGameAssets,
} from './lib/lazyAssets.js'
import { matchRoute, routes } from './lib/routes.js'
import AnalyticsConsent from './components/AnalyticsConsent.jsx'
import { trackPageView } from './lib/googleAnalytics.js'
import { updatePageMetadata } from './lib/seo.js'
import i18n from './i18n/index.js'
import PackHomeScreen from './screens/PackHomeScreen.jsx'

const PackJoinScreen = lazy(() => import('./screens/PackJoinScreen.jsx'))
const LegalScreen = lazy(() => import('./screens/LegalScreen.jsx'))
const loadGameCommonStyles = () => import('./game-common.css')

const gameAssets = {
  glitch: {
    style: () => Promise.resolve(),
    en: () => import('./i18n/locales/en/glitch.js'),
    de: () => import('./i18n/locales/de/glitch.js'),
  },
  pitch: {
    style: () => import('./pitch-redesign.css'),
    en: () => import('./i18n/locales/en/pitch.js'),
    de: () => import('./i18n/locales/de/pitch.js'),
  },
  heist: {
    style: () => import('./heist.css'),
    en: () => import('./i18n/locales/en/heist.js'),
    de: () => import('./i18n/locales/de/heist.js'),
  },
  alibi: {
    style: () => import('./alibi.css'),
    en: () => import('./i18n/locales/en/alibi.js'),
    de: () => import('./i18n/locales/de/alibi.js'),
  },
  drawback: {
    style: () => import('./drawback.css'),
    en: () => import('./i18n/locales/en/drawback.js'),
    de: () => import('./i18n/locales/de/drawback.js'),
  },
  deadAir: {
    style: () => import('./dead-air.css'),
    en: () => import('./i18n/locales/en/deadAir.js'),
    de: () => import('./i18n/locales/de/deadAir.js'),
  },
  noOffense: {
    style: () => import('./no-offense.css'),
    en: () => import('./i18n/locales/en/noOffense.js'),
    de: () => import('./i18n/locales/de/noOffense.js'),
  },
  secretMenu: {
    style: () => import('./secret-menu.css'),
    en: () => import('./i18n/locales/en/secretMenu.js'),
    de: () => import('./i18n/locales/de/secretMenu.js'),
  },
  badAdvice: {
    style: () => import('./bad-advice.css'),
    en: () => import('./i18n/locales/en/badAdvice.js'),
    de: () => import('./i18n/locales/de/badAdvice.js'),
  },
  unsent: {
    style: () => import('./unsent.css'),
    en: () => import('./i18n/locales/en/unsent.js'),
    de: () => import('./i18n/locales/de/unsent.js'),
  },
  openTabs: {
    style: () => import('./open-tabs.css'),
    en: () => import('./i18n/locales/en/openTabs.js'),
    de: () => import('./i18n/locales/de/openTabs.js'),
  },
  sideQuest: {
    style: () => import('./side-quest.css'),
    en: () => import('./i18n/locales/en/sideQuest.js'),
    de: () => import('./i18n/locales/de/sideQuest.js'),
  },
  provenance: {
    style: () => import('./provenance.css'),
    en: () => import('./i18n/locales/en/provenance.js'),
    de: () => import('./i18n/locales/de/provenance.js'),
  },
  sayMore: {
    style: () => import('./say-more.css'),
    en: () => import('./i18n/locales/en/sayMore.js'),
    de: () => import('./i18n/locales/de/sayMore.js'),
  },
  receipts: {
    style: () => import('./receipts.css'),
    en: () => import('./i18n/locales/en/receipts.js'),
    de: () => import('./i18n/locales/de/receipts.js'),
  },
}

async function loadGameScreen(screenLoader, namespace) {
  const assets = gameAssets[namespace]
  try {
    const [screen, , , english, german] = await waitForGameAssets(Promise.all([
      screenLoader(),
      loadGameCommonStyles(),
      assets.style(),
      assets.en(),
      assets.de(),
    ]), namespace)
    i18n.addResourceBundle('en', namespace, english.default, true, true)
    i18n.addResourceBundle('de', namespace, german.default, true, true)
    completeGameAssetLoad(namespace)
    return screen
  } catch (error) {
    return recoverGameAssetLoad(error, namespace)
  }
}

const gameScreen = (screenLoader, namespace) => lazy(() => loadGameScreen(screenLoader, namespace))

const GameHomeScreen = gameScreen(() => import('./screens/HomeScreen.jsx'), 'glitch')
const HostScreen = gameScreen(() => import('./screens/HostScreen.jsx'), 'glitch')
const JoinScreen = gameScreen(() => import('./screens/JoinScreen.jsx'), 'glitch')
const PlayerScreen = gameScreen(() => import('./screens/PlayerScreen.jsx'), 'glitch')
const PitchHomeScreen = gameScreen(() => import('./screens/PitchHomeScreen.jsx'), 'pitch')
const PitchHostScreen = gameScreen(() => import('./screens/PitchHostScreen.jsx'), 'pitch')
const PitchJoinScreen = gameScreen(() => import('./screens/PitchJoinScreen.jsx'), 'pitch')
const PitchPlayerScreen = gameScreen(() => import('./screens/PitchPlayerScreen.jsx'), 'pitch')
const HeistHomeScreen = gameScreen(() => import('./screens/HeistHomeScreen.jsx'), 'heist')
const HeistHostScreen = gameScreen(() => import('./screens/HeistHostScreen.jsx'), 'heist')
const HeistJoinScreen = gameScreen(() => import('./screens/HeistJoinScreen.jsx'), 'heist')
const HeistPlayerScreen = gameScreen(() => import('./screens/HeistPlayerScreen.jsx'), 'heist')
const AlibiHomeScreen = gameScreen(() => import('./screens/AlibiHomeScreen.jsx'), 'alibi')
const AlibiHostScreen = gameScreen(() => import('./screens/AlibiHostScreen.jsx'), 'alibi')
const AlibiJoinScreen = gameScreen(() => import('./screens/AlibiJoinScreen.jsx'), 'alibi')
const AlibiPlayerScreen = gameScreen(() => import('./screens/AlibiPlayerScreen.jsx'), 'alibi')
const DrawbackHomeScreen = gameScreen(() => import('./screens/DrawbackHomeScreen.jsx'), 'drawback')
const DrawbackHostScreen = gameScreen(() => import('./screens/DrawbackHostScreen.jsx'), 'drawback')
const DrawbackJoinScreen = gameScreen(() => import('./screens/DrawbackJoinScreen.jsx'), 'drawback')
const DrawbackPlayerScreen = gameScreen(() => import('./screens/DrawbackPlayerScreen.jsx'), 'drawback')
const DeadAirHomeScreen = gameScreen(() => import('./screens/DeadAirHomeScreen.jsx'), 'deadAir')
const DeadAirHostScreen = gameScreen(() => import('./screens/DeadAirHostScreen.jsx'), 'deadAir')
const DeadAirJoinScreen = gameScreen(() => import('./screens/DeadAirJoinScreen.jsx'), 'deadAir')
const DeadAirPlayerScreen = gameScreen(() => import('./screens/DeadAirPlayerScreen.jsx'), 'deadAir')
const NoOffenseHomeScreen = gameScreen(() => import('./screens/NoOffenseHomeScreen.jsx'), 'noOffense')
const NoOffenseHostScreen = gameScreen(() => import('./screens/NoOffenseHostScreen.jsx'), 'noOffense')
const NoOffenseJoinScreen = gameScreen(() => import('./screens/NoOffenseJoinScreen.jsx'), 'noOffense')
const NoOffensePlayerScreen = gameScreen(() => import('./screens/NoOffensePlayerScreen.jsx'), 'noOffense')
const SecretMenuHomeScreen = gameScreen(() => import('./screens/SecretMenuHomeScreen.jsx'), 'secretMenu')
const SecretMenuHostScreen = gameScreen(() => import('./screens/SecretMenuHostScreen.jsx'), 'secretMenu')
const SecretMenuJoinScreen = gameScreen(() => import('./screens/SecretMenuJoinScreen.jsx'), 'secretMenu')
const SecretMenuPlayerScreen = gameScreen(() => import('./screens/SecretMenuPlayerScreen.jsx'), 'secretMenu')
const BadAdviceHomeScreen = gameScreen(() => import('./screens/BadAdviceHomeScreen.jsx'), 'badAdvice')
const BadAdviceHostScreen = gameScreen(() => import('./screens/BadAdviceHostScreen.jsx'), 'badAdvice')
const BadAdviceJoinScreen = gameScreen(() => import('./screens/BadAdviceJoinScreen.jsx'), 'badAdvice')
const BadAdvicePlayerScreen = gameScreen(() => import('./screens/BadAdvicePlayerScreen.jsx'), 'badAdvice')
const UnsentHomeScreen = gameScreen(() => import('./screens/UnsentHomeScreen.jsx'), 'unsent')
const UnsentHostScreen = gameScreen(() => import('./screens/UnsentHostScreen.jsx'), 'unsent')
const UnsentJoinScreen = gameScreen(() => import('./screens/UnsentJoinScreen.jsx'), 'unsent')
const UnsentPlayerScreen = gameScreen(() => import('./screens/UnsentPlayerScreen.jsx'), 'unsent')
const OpenTabsHomeScreen = gameScreen(() => import('./screens/OpenTabsHomeScreen.jsx'), 'openTabs')
const OpenTabsHostScreen = gameScreen(() => import('./screens/OpenTabsHostScreen.jsx'), 'openTabs')
const OpenTabsJoinScreen = gameScreen(() => import('./screens/OpenTabsJoinScreen.jsx'), 'openTabs')
const OpenTabsPlayerScreen = gameScreen(() => import('./screens/OpenTabsPlayerScreen.jsx'), 'openTabs')
const SideQuestHomeScreen = gameScreen(() => import('./screens/SideQuestHomeScreen.jsx'), 'sideQuest')
const SideQuestHostScreen = gameScreen(() => import('./screens/SideQuestHostScreen.jsx'), 'sideQuest')
const SideQuestJoinScreen = gameScreen(() => import('./screens/SideQuestJoinScreen.jsx'), 'sideQuest')
const SideQuestPlayerScreen = gameScreen(() => import('./screens/SideQuestPlayerScreen.jsx'), 'sideQuest')
const ProvenanceHomeScreen = gameScreen(() => import('./screens/ProvenanceHomeScreen.jsx'), 'provenance')
const ProvenanceHostScreen = gameScreen(() => import('./screens/ProvenanceHostScreen.jsx'), 'provenance')
const ProvenanceJoinScreen = gameScreen(() => import('./screens/ProvenanceJoinScreen.jsx'), 'provenance')
const ProvenancePlayerScreen = gameScreen(() => import('./screens/ProvenancePlayerScreen.jsx'), 'provenance')
const SayMoreHomeScreen = gameScreen(() => import('./screens/SayMoreHomeScreen.jsx'), 'sayMore')
const SayMoreHostScreen = gameScreen(() => import('./screens/SayMoreHostScreen.jsx'), 'sayMore')
const SayMoreJoinScreen = gameScreen(() => import('./screens/SayMoreJoinScreen.jsx'), 'sayMore')
const SayMorePlayerScreen = gameScreen(() => import('./screens/SayMorePlayerScreen.jsx'), 'sayMore')
const ReceiptsHomeScreen = gameScreen(() => import('./screens/ReceiptsHomeScreen.jsx'), 'receipts')
const ReceiptsHostScreen = gameScreen(() => import('./screens/ReceiptsHostScreen.jsx'), 'receipts')
const ReceiptsJoinScreen = gameScreen(() => import('./screens/ReceiptsJoinScreen.jsx'), 'receipts')
const ReceiptsPlayerScreen = gameScreen(() => import('./screens/ReceiptsPlayerScreen.jsx'), 'receipts')

function readRoute() {
  return matchRoute(window.location.pathname)
}

function NotFoundScreen({ navigate }) {
  const { t } = useTranslation('pack')
  return (
    <main className="pack-not-found">
      <div><span>{t('notFound.label')}</span><h1>{t('notFound.title').split('\n').map((line) => <span key={line}>{line}<br /></span>)}</h1><p>{t('notFound.copy')}</p><button type="button" onClick={() => navigate(routes.pack)}>{t('notFound.back')}</button></div>
    </main>
  )
}

function AssetLoadErrorScreen() {
  const { t } = useTranslation('common')
  return (
    <main className="connection-screen">
      <section className="connection-card connection-card--error asset-load-error" role="alert">
        <span className="error-face">↻</span>
        <h1>{t('loading.failedTitle')}</h1>
        <p>{t('loading.failedMessage')}</p>
        <button type="button" onClick={forceGameAssetRefresh}>{t('loading.retry')}</button>
      </section>
    </main>
  )
}

class LazyLoadBoundary extends Component {
  state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  render() {
    if (this.state.failed) return <AssetLoadErrorScreen />
    return this.props.children
  }
}

function App() {
  const [route, setRoute] = useState(readRoute)
  const { t, i18n } = useTranslation(['pack', 'common'])

  useEffect(() => {
    if (route.canonicalPath) {
      window.history.replaceState({}, '', `${route.canonicalPath}${window.location.search}`)
      setRoute(readRoute())
      return
    }
    updatePageMetadata(route, t, i18n.resolvedLanguage)
    document.documentElement.dataset.experience = route.name === 'pack' ? 'pack' : 'game'
    trackPageView()
  }, [route, t, i18n.resolvedLanguage])

  useEffect(() => {
    const handleNavigation = () => setRoute(readRoute())
    window.addEventListener('popstate', handleNavigation)
    return () => window.removeEventListener('popstate', handleNavigation)
  }, [])

  const navigate = (path, { replace = false } = {}) => {
    window.history[replace ? 'replaceState' : 'pushState']({}, '', path)
    setRoute(readRoute())
    window.scrollTo({ top: 0, behavior: 'instant' })
  }

  let screen
  if (route.name === 'pack') screen = <PackHomeScreen navigate={navigate} />
  else if (route.name === 'imprint') screen = <LegalScreen page="imprint" navigate={navigate} />
  else if (route.name === 'privacy') screen = <LegalScreen page="privacy" navigate={navigate} />
  else if (route.name === 'pack-join') screen = <PackJoinScreen navigate={navigate} />
  else if (route.name === 'game') screen = <GameHomeScreen navigate={navigate} />
  else if (route.name === 'host') screen = <HostScreen code={route.code} navigate={navigate} />
  else if (route.name === 'join') screen = <JoinScreen navigate={navigate} />
  else if (route.name === 'player') screen = <PlayerScreen code={route.code} navigate={navigate} />
  else if (route.name === 'pitch-game') screen = <PitchHomeScreen navigate={navigate} />
  else if (route.name === 'pitch-join') screen = <PitchJoinScreen navigate={navigate} />
  else if (route.name === 'pitch-host') screen = <PitchHostScreen code={route.code} navigate={navigate} />
  else if (route.name === 'pitch-player') screen = <PitchPlayerScreen code={route.code} navigate={navigate} />
  else if (route.name === 'heist-game') screen = <HeistHomeScreen navigate={navigate} />
  else if (route.name === 'heist-join') screen = <HeistJoinScreen navigate={navigate} />
  else if (route.name === 'heist-host') screen = <HeistHostScreen code={route.code} navigate={navigate} />
  else if (route.name === 'heist-player') screen = <HeistPlayerScreen code={route.code} navigate={navigate} />
  else if (route.name === 'alibi-game') screen = <AlibiHomeScreen navigate={navigate} />
  else if (route.name === 'alibi-join') screen = <AlibiJoinScreen navigate={navigate} />
  else if (route.name === 'alibi-host') screen = <AlibiHostScreen code={route.code} navigate={navigate} />
  else if (route.name === 'alibi-player') screen = <AlibiPlayerScreen code={route.code} navigate={navigate} />
  else if (route.name === 'drawback-game') screen = <DrawbackHomeScreen navigate={navigate} />
  else if (route.name === 'drawback-join') screen = <DrawbackJoinScreen navigate={navigate} />
  else if (route.name === 'drawback-host') screen = <DrawbackHostScreen code={route.code} navigate={navigate} />
  else if (route.name === 'drawback-player') screen = <DrawbackPlayerScreen code={route.code} navigate={navigate} />
  else if (route.name === 'dead-air-game') screen = <DeadAirHomeScreen navigate={navigate} />
  else if (route.name === 'dead-air-join') screen = <DeadAirJoinScreen navigate={navigate} />
  else if (route.name === 'dead-air-host') screen = <DeadAirHostScreen code={route.code} navigate={navigate} />
  else if (route.name === 'dead-air-player') screen = <DeadAirPlayerScreen code={route.code} navigate={navigate} />
  else if (route.name === 'no-offense-game') screen = <NoOffenseHomeScreen navigate={navigate} />
  else if (route.name === 'no-offense-join') screen = <NoOffenseJoinScreen navigate={navigate} />
  else if (route.name === 'no-offense-host') screen = <NoOffenseHostScreen code={route.code} navigate={navigate} />
  else if (route.name === 'no-offense-player') screen = <NoOffensePlayerScreen code={route.code} navigate={navigate} />
  else if (route.name === 'secret-menu-game') screen = <SecretMenuHomeScreen navigate={navigate} />
  else if (route.name === 'secret-menu-join') screen = <SecretMenuJoinScreen navigate={navigate} />
  else if (route.name === 'secret-menu-host') screen = <SecretMenuHostScreen code={route.code} navigate={navigate} />
  else if (route.name === 'secret-menu-player') screen = <SecretMenuPlayerScreen code={route.code} navigate={navigate} />
  else if (route.name === 'bad-advice-game') screen = <BadAdviceHomeScreen navigate={navigate} />
  else if (route.name === 'bad-advice-join') screen = <BadAdviceJoinScreen navigate={navigate} />
  else if (route.name === 'bad-advice-host') screen = <BadAdviceHostScreen code={route.code} navigate={navigate} />
  else if (route.name === 'bad-advice-player') screen = <BadAdvicePlayerScreen code={route.code} navigate={navigate} />
  else if (route.name === 'unsent-game') screen = <UnsentHomeScreen navigate={navigate} />
  else if (route.name === 'unsent-join') screen = <UnsentJoinScreen navigate={navigate} />
  else if (route.name === 'unsent-host') screen = <UnsentHostScreen code={route.code} navigate={navigate} />
  else if (route.name === 'unsent-player') screen = <UnsentPlayerScreen code={route.code} navigate={navigate} />
  else if (route.name === 'open-tabs-game') screen = <OpenTabsHomeScreen navigate={navigate} />
  else if (route.name === 'open-tabs-join') screen = <OpenTabsJoinScreen navigate={navigate} />
  else if (route.name === 'open-tabs-host') screen = <OpenTabsHostScreen code={route.code} navigate={navigate} />
  else if (route.name === 'open-tabs-player') screen = <OpenTabsPlayerScreen code={route.code} navigate={navigate} />
  else if (route.name === 'side-quest-game') screen = <SideQuestHomeScreen navigate={navigate} />
  else if (route.name === 'side-quest-join') screen = <SideQuestJoinScreen navigate={navigate} />
  else if (route.name === 'side-quest-host') screen = <SideQuestHostScreen code={route.code} navigate={navigate} />
  else if (route.name === 'side-quest-player') screen = <SideQuestPlayerScreen code={route.code} navigate={navigate} />
  else if (route.name === 'provenance-game') screen = <ProvenanceHomeScreen navigate={navigate} />
  else if (route.name === 'provenance-join') screen = <ProvenanceJoinScreen navigate={navigate} />
  else if (route.name === 'provenance-host') screen = <ProvenanceHostScreen code={route.code} navigate={navigate} />
  else if (route.name === 'provenance-player') screen = <ProvenancePlayerScreen code={route.code} navigate={navigate} />
  else if (route.name === 'say-more-game') screen = <SayMoreHomeScreen navigate={navigate} />
  else if (route.name === 'say-more-join') screen = <SayMoreJoinScreen navigate={navigate} />
  else if (route.name === 'say-more-host') screen = <SayMoreHostScreen code={route.code} navigate={navigate} />
  else if (route.name === 'say-more-player') screen = <SayMorePlayerScreen code={route.code} navigate={navigate} />
  else if (route.name === 'receipts-game') screen = <ReceiptsHomeScreen navigate={navigate} />
  else if (route.name === 'receipts-join') screen = <ReceiptsJoinScreen navigate={navigate} />
  else if (route.name === 'receipts-host') screen = <ReceiptsHostScreen code={route.code} navigate={navigate} />
  else if (route.name === 'receipts-player') screen = <ReceiptsPlayerScreen code={route.code} navigate={navigate} />
  else screen = <NotFoundScreen navigate={navigate} />

  return <><LanguageSwitcher /><LazyLoadBoundary key={route.name}><Suspense fallback={<LoadingCard message={t('loading.connecting', { ns: 'common' })} />}>{screen}</Suspense></LazyLoadBoundary><AnalyticsConsent /></>
}

export default App
